import { useState, useEffect, Fragment } from "react";
import { Box, Typography, CircularProgress, InputAdornment, OutlinedInput } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import NavigateBeforeRoundedIcon from "@mui/icons-material/NavigateBeforeRounded";
import NavigateNextRoundedIcon from "@mui/icons-material/NavigateNextRounded";
import strings from "../../i18n/language";
import { getAuditAction } from "../../redux/actions/audit";

const BRAND = '#3b95e8';
const GLASS = 'rgba(255,255,255,0.04)';
const BORDER = 'rgba(255,255,255,0.08)';
const MUTED = 'rgba(232,240,254,0.38)';

const ROW_PER_PAGE = 20;
const SEARCH_DELAY_MS = 400;
const COLUMNS = 7;

const TABLE_FILTERS = [
  { value: null, label: strings.LABEL_AUDIT_ALL_TABLES },
  { value: "Card", label: "Card" },
  { value: "History", label: "History" },
  { value: "User", label: "User" },
];

const OPERATION_FILTERS = [
  { value: null, label: strings.LABEL_AUDIT_ALL_OPERATIONS },
  { value: "CREATE", label: strings.LABEL_AUDIT_CREATE },
  { value: "UPDATE", label: strings.LABEL_AUDIT_UPDATE },
  { value: "DELETE", label: strings.LABEL_AUDIT_DELETE },
];

const OPERATION_META = {
  CREATE: { bg: 'rgba(91,191,181,0.14)', color: '#5bbfb5' },
  UPDATE: { bg: 'rgba(59,149,232,0.14)', color: '#3b95e8' },
  DELETE: { bg: 'rgba(224,92,92,0.14)', color: '#e05c5c' },
};

const card = {
  background: GLASS,
  border: `1px solid ${BORDER}`,
  borderRadius: 20,
  backdropFilter: 'blur(12px)',
  overflow: 'hidden',
};

const FilterChip = ({ label, active, onClick }) => (
  <button onClick={onClick} style={{
    background: active ? 'rgba(59,149,232,0.15)' : 'rgba(255,255,255,0.04)',
    border: `1px solid ${active ? 'rgba(59,149,232,0.5)' : BORDER}`,
    borderRadius: 8,
    color: active ? BRAND : MUTED,
    fontSize: 12,
    fontWeight: active ? 600 : 400,
    padding: '5px 12px',
    cursor: 'pointer',
    transition: 'all 0.15s',
    letterSpacing: 0.2,
  }}>
    {label}
  </button>
);

const OperationBadge = ({ operation }) => {
  const meta = OPERATION_META[operation] || { bg: GLASS, color: MUTED };
  const label = OPERATION_FILTERS.find((f) => f.value === operation)?.label || operation;
  return (
    <span style={{
      background: meta.bg,
      color: meta.color,
      borderRadius: 6,
      padding: '3px 9px',
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: 0.3,
      whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );
};

const ColHeader = ({ children, style }) => (
  <th style={{
    textAlign: 'left',
    fontSize: 11,
    fontWeight: 600,
    color: MUTED,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    padding: '14px 16px',
    borderBottom: `1px solid ${BORDER}`,
    ...style,
  }}>
    {children}
  </th>
);

const Td = ({ children, style }) => (
  <td style={{
    padding: '13px 16px',
    fontSize: 13,
    color: 'rgba(232,240,254,0.75)',
    borderBottom: `1px solid rgba(255,255,255,0.04)`,
    verticalAlign: 'middle',
    ...style,
  }}>
    {children}
  </td>
);

// The API sends the saved values as objects; a malformed one stays raw text
const asObject = (value) => (value && typeof value === 'object' ? value : {});
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const recordId = (entry) => {
  const id = asObject(entry.newValues).id ?? asObject(entry.oldValues).id;
  return id ? `#${id}` : '—';
};
const show = (value) => {
  const text = value === undefined ? '—' : JSON.stringify(value);
  return text.length > 40 ? `${text.slice(0, 40)}…` : text;
};

// What a row says about an entry: the changed fields for an update, the
// linked ids (user_id, card_id…) for a creation or a deletion
const summary = (entry) => {
  const before = asObject(entry.oldValues);
  const after = asObject(entry.newValues);
  if (entry.operation === "UPDATE") {
    return Object.keys({ ...before, ...after })
      .filter((key) => !same(before[key], after[key]))
      .map((key) => `${key}: ${show(before[key])} → ${show(after[key])}`)
      .join(' · ') || '—';
  }
  const values = entry.operation === "DELETE" ? before : after;
  return Object.keys(values)
    .filter((key) => key.endsWith('_id') && values[key] !== null)
    .map((key) => `${key}: ${show(values[key])}`)
    .join(' · ') || '—';
};

// Every field before and after, the changed ones highlighted
const Details = ({ entry }) => {
  const before = asObject(entry.oldValues);
  const after = asObject(entry.newValues);
  const cell = { padding: '6px 12px', fontSize: 12, fontFamily: 'monospace', wordBreak: 'break-all', verticalAlign: 'top' };
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={{ ...cell, width: '20%', textAlign: 'left', color: MUTED, fontFamily: 'inherit' }}></th>
          <th style={{ ...cell, width: '40%', textAlign: 'left', color: MUTED, fontFamily: 'inherit' }}>{strings.LABEL_AUDIT_BEFORE}</th>
          <th style={{ ...cell, width: '40%', textAlign: 'left', color: MUTED, fontFamily: 'inherit' }}>{strings.LABEL_AUDIT_AFTER}</th>
        </tr>
      </thead>
      <tbody>
        {Object.keys({ ...before, ...after }).map((key) => {
          const changed = entry.operation === "UPDATE" && !same(before[key], after[key]);
          const color = changed ? BRAND : 'rgba(232,240,254,0.6)';
          return (
            <tr key={key} style={{ background: changed ? 'rgba(59,149,232,0.08)' : 'transparent' }}>
              <td style={{ ...cell, color: MUTED }}>{key}</td>
              <td style={{ ...cell, color }}>{key in before ? JSON.stringify(before[key]) : '—'}</td>
              <td style={{ ...cell, color }}>{key in after ? JSON.stringify(after[key]) : '—'}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const AuditList = () => {
  const [page, setPage] = useState(0);
  const [table, setTable] = useState(null);
  const [operation, setOperation] = useState(null);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState(null);
  const dispatch = useDispatch();

  const entries = useSelector((state) => state.audit.entries);
  const total = useSelector((state) => state.audit.total);
  const isLoadingGetAudit = useSelector((state) => state.audit.isLoadingGetAudit);

  // The search runs once typing pauses, from the first page
  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(search.trim());
      setPage(0);
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    dispatch(getAuditAction(ROW_PER_PAGE, page * ROW_PER_PAGE, { table, operation, q: query }));
  }, [dispatch, page, table, operation, query]);

  const handleTable = (value) => {
    setTable(value);
    setPage(0);
  };

  const handleOperation = (value) => {
    setOperation(value);
    setPage(0);
  };

  const totalPages = Math.ceil(total / ROW_PER_PAGE);

  return (
    <Box sx={{ width: "100%" }}>
      <div style={card}>

        {/* Header + Filters */}
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${BORDER}` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <Typography style={{ fontSize: 15, fontWeight: 700, color: '#e8f0fe', letterSpacing: 0.2 }}>
              {strings.LABEL_AUDIT_TITLE}
            </Typography>
            {total > 0 && (
              <span style={{ fontSize: 12, color: MUTED }}>
                {total} {strings.LABEL_AUDIT_TOTAL}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            {TABLE_FILTERS.map((f) => (
              <FilterChip
                key={f.value ?? 'all'}
                label={f.label}
                active={table === f.value}
                onClick={() => handleTable(f.value)}
              />
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            {OPERATION_FILTERS.map((f) => (
              <FilterChip
                key={f.value ?? 'all'}
                label={f.label}
                active={operation === f.value}
                onClick={() => handleOperation(f.value)}
              />
            ))}
          </div>
          <OutlinedInput
            fullWidth
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={strings.LABEL_AUDIT_SEARCH}
            startAdornment={(
              <InputAdornment position="start">
                <SearchRoundedIcon style={{ fontSize: 18, color: MUTED }} />
              </InputAdornment>
            )}
          />
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <ColHeader>{strings.LABEL_AUDIT_DATE}</ColHeader>
                <ColHeader>{strings.LABEL_AUDIT_TABLE}</ColHeader>
                <ColHeader>{strings.LABEL_AUDIT_OPERATION}</ColHeader>
                <ColHeader>{strings.LABEL_AUDIT_RECORD}</ColHeader>
                <ColHeader>{strings.LABEL_AUDIT_CHANGES}</ColHeader>
                <ColHeader>{strings.LABEL_AUDIT_USER}</ColHeader>
                <ColHeader>IP</ColHeader>
              </tr>
            </thead>
            <tbody>
              {isLoadingGetAudit && (
                <tr>
                  <td colSpan={COLUMNS} style={{ textAlign: 'center', padding: 40 }}>
                    <CircularProgress size={24} sx={{ color: BRAND }} />
                  </td>
                </tr>
              )}
              {!isLoadingGetAudit && entries.length === 0 && (
                <tr>
                  <td colSpan={COLUMNS} style={{ textAlign: 'center', padding: 40, fontSize: 13, color: MUTED }}>
                    {strings.LABEL_AUDIT_EMPTY}
                  </td>
                </tr>
              )}
              {!isLoadingGetAudit && entries.map((entry) => (
                <Fragment key={entry.id}>
                  <tr
                    style={{ transition: 'background 0.1s', cursor: 'pointer' }}
                    onClick={() => setOpenId(openId === entry.id ? null : entry.id)}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <Td style={{ color: MUTED, whiteSpace: 'nowrap', fontSize: 12 }}>
                      {dayjs(entry.timestamp).format("DD/MM/YYYY HH:mm:ss")}
                    </Td>
                    <Td>{entry.tableName}</Td>
                    <Td><OperationBadge operation={entry.operation} /></Td>
                    <Td style={{ color: MUTED, fontSize: 12 }}>
                      {recordId(entry)}
                    </Td>
                    <Td style={{ fontSize: 12, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                      {summary(entry)}
                    </Td>
                    <Td style={{ fontSize: 12, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                      {entry.userId || <span style={{ color: MUTED, fontFamily: 'inherit' }}>{strings.LABEL_AUDIT_SYSTEM}</span>}
                    </Td>
                    <Td style={{ color: MUTED, fontSize: 12, whiteSpace: 'nowrap' }}>{entry.ip || '—'}</Td>
                  </tr>
                  {openId === entry.id && (
                    <tr>
                      <td colSpan={COLUMNS} style={{ padding: '8px 16px 16px', background: 'rgba(0,0,0,0.15)', borderBottom: `1px solid ${BORDER}` }}>
                        <Details entry={entry} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, padding: '12px 24px' }}>
            <span style={{ fontSize: 12, color: MUTED }}>
              {page + 1} / {totalPages}
            </span>
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 0}
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: `1px solid ${BORDER}`,
                borderRadius: 8,
                color: page === 0 ? 'rgba(255,255,255,0.15)' : '#e8f0fe',
                cursor: page === 0 ? 'not-allowed' : 'pointer',
                width: 32, height: 32,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <NavigateBeforeRoundedIcon style={{ fontSize: 18 }} />
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page >= totalPages - 1}
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: `1px solid ${BORDER}`,
                borderRadius: 8,
                color: page >= totalPages - 1 ? 'rgba(255,255,255,0.15)' : '#e8f0fe',
                cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer',
                width: 32, height: 32,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <NavigateNextRoundedIcon style={{ fontSize: 18 }} />
            </button>
          </div>
        )}
      </div>
    </Box>
  );
};

export default AuditList;
